import React, { useEffect, useState } from "react";
import { IFeedbackJob } from "../types";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { useGetAllSuggestedTalents } from "features/home/hooks/useGetAllSuggestedTalents";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useGetJobListingApplicants } from "features/home/hooks/useGetApplicants";

interface HiredTalentsScreenProps {
  job: IFeedbackJob;
  onBack: () => void;
  onSubmit: (talents: string[]) => void;
}

const HiredTalentsScreen: React.FC<HiredTalentsScreenProps> = ({
  job,
  onBack,
  onSubmit,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [candidateState, setCandidateState] = useState(false);
  const [filteredCandidates, setFilteredCandidates] = useState<Talents[]>([]);
  const { token } = useGetUserInfo();
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);


  // const { data: shortlistedData, isLoading: isLoadingShortlisted } = useGetShortListed({ "id": job.id+"", token });
  const { data: allSuggestedTalentsData, isLoading: isLoadingAllSuggested } = useGetAllSuggestedTalents({ "token":token, "id": job.id+"" });
  const { data: applicants, isLoading: isLoadingApplicants } = useGetJobListingApplicants({ "id": job.id+"", "token":token });


  const toggleCandidate = (id: string) => {
    if (selectedCandidates.includes(id)) {
      setSelectedCandidates(selectedCandidates.filter((candidateId) => candidateId !== id));
    } else {
      setSelectedCandidates([...selectedCandidates, id]);
    }
  };

  const handleSubmit = () => {
    onSubmit(selectedCandidates);
  };

  interface Talent {
    first_name: string,
    last_name: string,
    email: string
  }
   interface Talents {
    talent: Talent,
    id: string,
    talent_id: string
  }
  const candidatesSuggested: Talents[] = allSuggestedTalentsData?.data || [];
  const candidatesApplied: Talents[] = applicants?.data || [];

  const filteredCandidatesApplied = candidatesApplied?.filter(
    (candidate) =>
      candidate.talent.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate?.talent.last_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const filteredCandidatesSuggested = candidatesSuggested?.filter(
    (candidate) =>
      candidate.talent.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate?.talent.last_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (candidateState) {
      setFilteredCandidates(filteredCandidatesSuggested);
    } else {
      setFilteredCandidates(filteredCandidatesApplied);
    }
  }, [filteredCandidatesApplied, filteredCandidatesApplied, candidateState]);
  
  
  const getClosedExpiredDate = () => {
    if (job.closed_at) return formatDate(job.closed_at);
    if (job.expired === 1 || job.expired === "1" || job.expired === true) {
      return job.expiry_date ? formatDate(job.expiry_date) : "Expired";
    }
    return "N/A";
  };

  return (
    <div className="mb-6">
      <div className="flex items-center mb-2">
        <i className="ri-briefcase-line text-xl text-primary mr-2"></i>
        <span className="text-sm text-gray-500">
          Jobs <i className="ri-arrow-right-s-line mx-1"></i> Recruitment
          Feedback
        </span>
      </div>
      <div className="border rounded-xl bg-white p-6 mx-auto w-full">
        <div className="border rounded-lg p-6 mb-8 bg-gray-50">
          <div className="flex justify-between mb-4">
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Job Title</p>
              <p className="font-medium text-lg">{job.title || "N/A"}</p>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Posted</p>
              <p className="font-medium">{formatDate(job.created_at)}</p>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Closed/Expired</p>
              <p className="font-medium">{getClosedExpiredDate()}</p>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Applications</p>
              <p className="font-medium">
                {job.likes_count || "0"} candidates
              </p>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-sm">Matched Talents</p>
              <p className="font-medium">
                {job.recommendations_count || "0"} talents
              </p>
            </div>
            <div className="flex-1"></div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Who did you hire? (Select all that apply)
          </h2>

          <div className="flex border-b border-gray-200 mb-4">
            <button onClick={()=>{
              setCandidateState(false);
            }} className={candidateState?"px-4 py-2 text-gray-500 hover:text-gray-700":"px-4 py-2 border-b-2 border-primary text-primary font-medium"}>
              Applied
            </button>
            <button onClick={()=>{
              setCandidateState(true);
            }} className={!candidateState?"px-4 py-2 text-gray-500 hover:text-gray-700":"px-4 py-2 border-b-2 border-primary text-primary font-medium"}>
              Matched
            </button>
          </div>

          <div className="mb-6">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="space-y-3">
            {filteredCandidates.length > 0 ? (
              <>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Talents:
                </p>
                {filteredCandidates.map((candidate) => (
                  <label
                    key={candidate.talent_id}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all`}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="mr-3"
                        checked={selectedCandidates.includes(candidate.talent_id)}
                        onChange={() => toggleCandidate(candidate.talent_id)}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {`${candidate.talent.first_name} ${candidate.talent.last_name}`}
                      </p>
                      <p className="text-sm text-gray-600">{candidate.talent.email}</p>
                    </div>
                  </label>
                ))}
              </>
            ) : (
              <div className="text-center py-8 border rounded-lg bg-gray-50">
                <i className="ri-user-search-line text-3xl text-gray-300 mb-3"></i>
                <p className="text-gray-600 font-medium mb-1">
                  No candidates found
                </p>
                <p className="text-gray-500 text-sm">
                  {searchTerm
                    ? `No candidates match "${searchTerm}"`
                    : "No candidates were found for this job posting."}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center pt-6 border-t">
          <button
            onClick={onBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-800 font-medium flex items-center"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={selectedCandidates.length === 0}
            className={`px-6 py-2.5 rounded-lg font-medium ${
              selectedCandidates.length > 0
                ? "bg-primary text-white hover:bg-blue-700"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Submit Feedback
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HiredTalentsScreen;
